import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Shield, X, ChevronDown, ChevronUp } from 'lucide-react';

const CONSENT_KEY = 'ns_cookie_consent';
const CATEGORIES = [
  { id: 'essential', label: 'Essential', description: 'Required for the website to function. Cannot be disabled.', locked: true },
  { id: 'analytics', label: 'Analytics', description: 'Help us understand how visitors interact with our website (e.g., PostHog, Google Analytics).', locked: false },
  { id: 'marketing', label: 'Marketing', description: 'Used for targeted advertising and retargeting (not currently active).', locked: false },
];

const getConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
};

const saveConsent = (consent) => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, timestamp: new Date().toISOString() }));
  window.dispatchEvent(new CustomEvent('cookie-consent-updated', { detail: consent }));
};

export const getConsentValue = (category) => {
  const consent = getConsent();
  if (!consent) return false;
  if (category === 'essential') return true;
  return consent[category] === true;
};

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({ essential: true, analytics: false, marketing: false });

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    const consent = { essential: true, analytics: true, marketing: true };
    saveConsent(consent);
    setVisible(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    const consent = { essential: true, analytics: false, marketing: false };
    saveConsent(consent);
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    saveConsent({ ...preferences, essential: true });
    setVisible(false);
  }, [preferences]);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6" data-testid="cookie-banner">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl border border-[#F0E6F6] overflow-hidden">
        <div className="p-5 md:p-6">
          <div className="flex items-start gap-3 mb-4">
            <Shield className="w-5 h-5 text-[#E84993] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-[#2A2A2A] mb-1">We value your privacy</h3>
              <p className="text-xs text-[#6A6A6A] leading-relaxed">
                We use cookies to enhance your experience, analyze site traffic, and for marketing purposes.
                You can manage your preferences or learn more in our{' '}
                <Link to="/privacy" className="text-[#E84993] underline hover:text-[#D13A7A]">Privacy Policy</Link>.
              </p>
            </div>
            <button onClick={handleRejectAll} className="text-[#9A9A9A] hover:text-[#2A2A2A] transition-colors" aria-label="Close" data-testid="cookie-close-btn">
              <X className="w-4 h-4" />
            </button>
          </div>

          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex items-center gap-1.5 text-xs text-[#7A7A7A] hover:text-[#E84993] transition-colors mb-4"
            data-testid="cookie-manage-btn"
          >
            {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            Manage preferences
          </button>

          {showDetails && (
            <div className="space-y-3 mb-5 pl-1" data-testid="cookie-preferences-panel">
              {CATEGORIES.map((cat) => (
                <label key={cat.id} className="flex items-center gap-3 cursor-pointer group">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={cat.locked || preferences[cat.id]}
                      disabled={cat.locked}
                      onChange={(e) => setPreferences((p) => ({ ...p, [cat.id]: e.target.checked }))}
                      className="sr-only peer"
                      data-testid={`cookie-toggle-${cat.id}`}
                    />
                    <div className={`w-9 h-5 rounded-full transition-colors duration-200 ${cat.locked ? 'bg-[#E84993] opacity-60' : 'bg-gray-200 peer-checked:bg-[#E84993]'}`} />
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${(cat.locked || preferences[cat.id]) ? 'translate-x-4' : ''}`} />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-[#2A2A2A]">{cat.label}</span>
                    {cat.locked && <span className="text-[10px] text-[#9A9A9A] ml-1.5">(Always on)</span>}
                    <p className="text-[11px] text-[#9A9A9A] leading-snug">{cat.description}</p>
                  </div>
                </label>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {showDetails ? (
              <button
                onClick={handleSavePreferences}
                className="flex-1 min-w-[120px] px-4 py-2.5 text-xs font-medium text-white rounded-full transition-colors"
                style={{ background: 'linear-gradient(135deg, #E84993, #D13A7A)' }}
                data-testid="cookie-save-btn"
              >
                Save Preferences
              </button>
            ) : null}
            <button
              onClick={handleRejectAll}
              className="flex-1 min-w-[100px] px-4 py-2.5 text-xs font-medium text-[#6A6A6A] bg-[#F5F5F5] rounded-full hover:bg-[#EBEBEB] transition-colors"
              data-testid="cookie-reject-btn"
            >
              Reject All
            </button>
            <button
              onClick={handleAcceptAll}
              className="flex-1 min-w-[100px] px-4 py-2.5 text-xs font-medium text-white rounded-full transition-colors"
              style={{ background: 'linear-gradient(135deg, #E84993, #D13A7A)' }}
              data-testid="cookie-accept-btn"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CookiePreferencesButton = ({ className = '' }) => {
  const handleOpen = () => {
    localStorage.removeItem(CONSENT_KEY);
    window.dispatchEvent(new Event('open-cookie-preferences'));
    window.location.reload();
  };

  return (
    <button
      onClick={handleOpen}
      className={`text-sm text-[#6A6A6A] hover:text-[#E84993] transition-colors duration-300 ${className}`}
      data-testid="cookie-preferences-link"
    >
      Cookie Preferences
    </button>
  );
};
